import { type FC } from 'react';
import { StepperOrderDetailsMonarchData } from 'components/ui/organisms/checkout-stepper/components/stepper-order-details/types';
import { getEnv } from 'env/env';
import {
  currencyOptions,
  isCurrencyAcceptedForPayment,
  resolveAcceptedCurrencies,
} from 'utils/currency';
import { MonarchDataConsumer } from 'utils/eventing/monarch/MonarchDataConsumer';
import './secure-checkout-badge.scss';

type SecureCheckoutBadgeProps = {
  currency?: string;
  className?: string;
  isMinimalLayout?: boolean;
};

const getCurrencyName = (
  currencyCode: string,
  acceptedCurrencies: readonly string[],
): string => {
  // Feature flag to enable dynamic currency display
  // Check this dynamically so it can be tested
  const isDynamicCurrencyEnabled =
    getEnv('NEXT_PUBLIC_ENABLE_DYNAMIC_CURRENCY', 'false') === 'true';

  // If feature flag is not enabled, always return US Dollar
  if (!isDynamicCurrencyEnabled) {
    return 'US Dollar';
  }

  // Only currencies checkout can settle in show dynamically; others default to USD
  if (!isCurrencyAcceptedForPayment(currencyCode, acceptedCurrencies)) {
    const usdCurrency = currencyOptions.find((option) => option.code === 'USD');
    return usdCurrency?.name ?? 'US Dollar';
  }

  const currency = currencyOptions.find(
    (option) => option.code === currencyCode.toUpperCase(),
  );
  return currency?.name ?? 'US Dollar';
};

const SecureCheckoutBadgeContent: FC<{
  currencyName: string;
  className: string;
  isMinimalLayout: boolean;
}> = ({ currencyName, className, isMinimalLayout }) => (
  <div
    className={`secure-checkout-badge ${isMinimalLayout ? 'secure-checkout-badge--minimal' : ''} ${className}`}
    data-testid="secure-checkout-badge"
  >
    <svg
      width="16"
      height="21"
      viewBox="0 0 16 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-testid="secure-checkout-icon"
    >
      <path
        d="M16 7.00002H13V5.21002C13 2.60002 11.09 0.270018 8.49 0.0200176C5.51 -0.259982 3 2.08002 3 5.00002V7.00002H0V21H16V7.00002ZM8 16C6.9 16 6 15.1 6 14C6 12.9 6.9 12 8 12C9.1 12 10 12.9 10 14C10 15.1 9.1 16 8 16ZM5 7.00002V5.00002C5 3.34002 6.34 2.00002 8 2.00002C9.66 2.00002 11 3.34002 11 5.00002V7.00002H5Z"
        fill="#0D7D4D"
      />
    </svg>
    <span
      className="secure-checkout-badge__text"
      data-testid="secure-checkout-text"
    >
      <span>Secure</span>{' '}
      <span
        className="secure-checkout-badge__currency"
        data-testid="secure-checkout-currency"
      >
        {currencyName}
      </span>{' '}
      <span>Checkout</span>
    </span>
  </div>
);

export const SecureCheckoutBadge: FC<SecureCheckoutBadgeProps> = ({
  currency = 'USD',
  className = '',
  isMinimalLayout = false,
}) => (
  <MonarchDataConsumer<StepperOrderDetailsMonarchData> experimentKey="checkout">
    {(monarchCartData) => {
      const multiCurrency = monarchCartData?.data?.multiCurrency;

      if (!multiCurrency?.enableMultiCurrency) {
        return null;
      }

      const currencyName = getCurrencyName(
        currency,
        resolveAcceptedCurrencies(multiCurrency.acceptedCurrencies),
      );

      return (
        <SecureCheckoutBadgeContent
          currencyName={currencyName}
          className={className}
          isMinimalLayout={isMinimalLayout}
        />
      );
    }}
  </MonarchDataConsumer>
);

export default SecureCheckoutBadge;
